import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, TrendingUp, TrendingDown, Wallet, Ticket, PoundSterling, Trophy, Download, AlertTriangle } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Cell, PieChart, Pie, Legend } from "recharts";

const statusBadge = {
  active: "bg-green-100 text-green-700",
  part_used: "bg-amber-100 text-amber-700",
  used: "bg-red-100 text-red-600",
};

function buildMonthlySeries(vouchers) {
  // Last 6 months revenue by purchase month
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleString("en-GB", { month: "short" }), revenue: 0 });
  }
  const idx = Object.fromEntries(months.map((m, i) => [m.key, i]));
  vouchers.forEach((v) => {
    // Only completed sales — exclude refunded/cancelled and abandoned/pending orders
    if (v.status !== "valid" && v.status !== "checked_in") return;
    if (!v.created_at) return;
    const d = new Date(v.created_at);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (key in idx) months[idx[key]].revenue += v.original_amount || 0;
  });
  return months.map((m) => ({ ...m, revenue: Math.round(m.revenue * 100) / 100 }));
}

function buildTopSellers(vouchers, topN) {
  const map = {};
  vouchers.forEach((v) => {
    const t = v.product_title || "Untitled";
    if (!map[t]) map[t] = { title: t, count: 0, revenue: 0, refunded: 0 };
    // Completed sales count toward the event; cancelled/refunded tracked separately.
    if (v.status === "valid" || v.status === "checked_in") {
      map[t].count += 1;
      map[t].revenue += v.original_amount || 0;
    } else if (v.status === "cancelled") {
      map[t].refunded += 1;
    }
  });
  return Object.values(map)
    .filter((m) => m.count > 0)
    .map((m) => ({ ...m, revenue: Math.round(m.revenue * 100) / 100 }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, topN);
}

const BAR_COLORS = ["#2ec9c9", "#4bd4b0", "#69c07a", "#f4b740", "#f47c7c", "#8b5cf6", "#3b82f6", "#ec4899", "#14b8a6", "#f97316"];
const barColor = (i) => BAR_COLORS[i % BAR_COLORS.length];

const CHANNEL_META = {
  online: { label: "Online", color: "#2ec9c9" },
  box_office: { label: "Box office", color: "#f4b740" },
  comp: { label: "Comp", color: "#9aa7b8" },
};

function channelOf(v) {
  if (v.is_comp) return "comp";
  if (v.source === "manual_paid") return "box_office";
  return "online";
}

function buildChannelTrend(vouchers, productId = "all") {
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleString("en-GB", { month: "short" }), online: 0, box_office: 0, comp: 0 });
  }
  const idx = Object.fromEntries(months.map((m, i) => [m.key, i]));
  vouchers.forEach((v) => {
    if (v.status !== "valid" && v.status !== "checked_in") return;
    if (productId !== "all" && v.product_id !== productId) return;
    if (!v.created_at) return;
    const d = new Date(v.created_at);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (!(key in idx)) return;
    months[idx[key]][channelOf(v)] += v.amount_paid || 0;
  });
  return months.map((m) => ({ ...m, online: Math.round(m.online * 100) / 100, box_office: Math.round(m.box_office * 100) / 100, comp: Math.round(m.comp * 100) / 100 }));
}

function aggregateChannels(attendance, productId = "all") {
  const totals = { online: { count: 0, revenue: 0 }, box_office: { count: 0, revenue: 0 }, comp: { count: 0, revenue: 0 } };
  Object.entries(attendance || {}).forEach(([pid, ev]) => {
    if (productId !== "all" && pid !== productId) return;
    const ch = ev.channels || {};
    ["online", "box_office", "comp"].forEach((k) => {
      totals[k].count += ch[k]?.count || 0;
      totals[k].revenue += ch[k]?.revenue || 0;
    });
  });
  return ["online", "box_office", "comp"].map((k) => ({
    key: k, name: CHANNEL_META[k].label, color: CHANNEL_META[k].color,
    count: totals[k].count, revenue: Math.round(totals[k].revenue * 100) / 100,
  }));
}

function downloadCsv(filename, rows) {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function SalesTab() {
  const [data, setData] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [pct, setPct] = useState(5);
  const [channelEvent, setChannelEvent] = useState("all");
  const [refunds, setRefunds] = useState(null);
  const [topN, setTopN] = useState(() => parseInt(localStorage.getItem("gp_sales_topn") || "20", 10));
  useEffect(() => {
    api.get("/business/vouchers").then(({ data }) => setData(data));
    api.get("/business/attendance").then(({ data }) => setAttendance(data)).catch(() => setAttendance({}));
    api.get("/business/payout").then(({ data }) => setPct(data.commission_pct)).catch(() => {});
    api.get("/business/refund-analytics").then(({ data }) => setRefunds(data)).catch(() => setRefunds({ events: [], totals: {} }));
  }, []);
  useEffect(() => { localStorage.setItem("gp_sales_topn", String(topN)); }, [topN]);
  if (!data) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  const series = buildMonthlySeries(data.vouchers);
  const topSellers = buildTopSellers(data.vouchers, topN);
  const thisMonth = series[series.length - 1]?.revenue || 0;
  const lastMonth = series[series.length - 2]?.revenue || 0;
  const momGrowth = lastMonth > 0 ? ((thisMonth - lastMonth) / lastMonth) * 100 : (thisMonth > 0 ? 100 : 0);
  const momUp = momGrowth >= 0;

  const exportCsv = () => {
    const rows = [["TicketsPro.app — Sales report"], ["Generated", new Date().toLocaleString("en-GB")], []];
    rows.push(["Monthly revenue (last 6 months)"]);
    rows.push(["Month", "Revenue (£)"]);
    series.forEach((m) => rows.push([m.label, m.revenue.toFixed(2)]));
    rows.push(["Month-over-month growth", `${momGrowth.toFixed(1)}%`]);
    rows.push([]);
    rows.push(["Top events by revenue"]);
    rows.push(["Rank", "Event", "Tickets sold", "Refunded", "Revenue (£)"]);
    topSellers.forEach((t, i) => rows.push([i + 1, t.title, t.count, t.refunded, t.revenue.toFixed(2)]));
    downloadCsv("ticketspro-sales-report.csv", rows);
  };

  const checkedIn = data.vouchers.filter((v) => v.status === "checked_in").length;
  const ticketsSold = data.vouchers.filter((v) => v.status === "valid" || v.status === "checked_in").length;
  const totalRevenue = data.vouchers
    .filter((v) => v.status === "valid" || v.status === "checked_in")
    .reduce((s, v) => s + (v.original_amount || 0), 0);
  const channels = aggregateChannels(attendance, channelEvent);
  const channelTotalRev = channels.reduce((s, c) => s + c.revenue, 0);
  const channelTotalCount = channels.reduce((s, c) => s + c.count, 0);
  const channelPie = channels.filter((c) => c.revenue > 0);
  const channelTrend = buildChannelTrend(data.vouchers, channelEvent);
  const trendHasData = channelTrend.some((m) => m.online || m.box_office || m.comp);
  const eventOptions = Object.values(data.vouchers.reduce((acc, v) => {
    if (v.product_id && !acc[v.product_id]) acc[v.product_id] = { id: v.product_id, title: v.product_title || "Untitled" };
    return acc;
  }, {}));
  const refundEvents = (refunds?.events || []).filter((e) => e.refunded > 0).slice(0, 12);
  const refundTotals = refunds?.totals || {};
  const refundReasons = (refunds?.reasons || []).slice(0, 6);
  const refundAlerts = refunds?.alerts || [];
  const alertThreshold = refunds?.alert_threshold ?? 15;
  const refundBarColor = (rate) => (rate >= 20 ? "#ef4444" : rate >= 10 ? "#f59e0b" : "#2ec9c9");
  const cards = [
    { label: "Total revenue", value: `£${totalRevenue.toFixed(2)}`, icon: PoundSterling },
    { label: "Tickets sold", value: ticketsSold, icon: Ticket },
    { label: "Checked in", value: checkedIn, icon: TrendingUp },
    { label: "Events", value: new Set(data.vouchers.map((v) => v.product_title)).size, icon: Wallet },
  ];

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={exportCsv} data-testid="export-analytics-csv" className="gp-btn-outline rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2">
          <Download size={16} /> Export report (CSV)
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
            <div className="w-10 h-10 rounded-xl bg-[#eefafa] flex items-center justify-center mb-3">
              <c.icon className="text-[#2ec9c9]" size={20} />
            </div>
            <p className="text-[#8a97a8] text-sm">{c.label}</p>
            <p className="text-2xl font-bold text-[#16243b] mt-1">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Revenue chart */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] mb-8" data-testid="revenue-chart-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-[#16243b]">Revenue over time</h3>
            <span data-testid="mom-growth-badge" className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${momUp ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
              {momUp ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
              {momUp ? "+" : ""}{momGrowth.toFixed(1)}% MoM
            </span>
          </div>
          <span className="text-xs text-[#8a97a8]">Last 6 months</span>
        </div>
        <p className="text-xs text-[#8a97a8] -mt-2 mb-4">
          This month £{thisMonth.toFixed(2)} vs last month £{lastMonth.toFixed(2)}
        </p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2ec9c9" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2ec9c9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#8a97a8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#8a97a8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(val) => [`£${Number(val).toFixed(2)}`, "Revenue"]} contentStyle={{ borderRadius: 12, border: "1px solid #eef1f5" }} />
              <Area type="monotone" dataKey="revenue" stroke="#2ec9c9" strokeWidth={3} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sales by channel (account-wide) */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] mb-8" data-testid="channel-chart-card">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h3 className="font-semibold text-[#16243b]">Sales by channel</h3>
          <select value={channelEvent} onChange={(e) => setChannelEvent(e.target.value)} data-testid="channel-event-filter"
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-[#2ec9c9] bg-white max-w-[240px]">
            <option value="all">All events</option>
            {eventOptions.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
          </select>
        </div>
        {attendance === null ? (
          <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={24} /></div>
        ) : channelTotalCount === 0 ? (
          <p className="py-8 text-center text-[#8a97a8]">No ticket activity yet.</p>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6 items-center">
            <div className="h-56" data-testid="channel-pie">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={channelPie} dataKey="revenue" nameKey="name" cx="50%" cy="50%" innerRadius={52} outerRadius={80} paddingAngle={2}>
                    {channelPie.map((c) => <Cell key={c.key} fill={c.color} />)}
                  </Pie>
                  <Tooltip formatter={(val, name) => [`£${Number(val).toFixed(2)}`, name]} contentStyle={{ borderRadius: 12, border: "1px solid #eef1f5" }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {channels.map((c) => (
                <div key={c.key} className="flex items-center justify-between p-3 rounded-xl bg-[#f6f8fb]" data-testid={`channel-row-${c.key}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ background: c.color }} />
                    <span className="font-medium text-[#16243b]">{c.name}</span>
                    <span className="text-xs text-[#8a97a8]">{c.count} ticket{c.count === 1 ? "" : "s"}</span>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className="font-bold text-[#16243b] text-sm">£{c.revenue.toFixed(2)}</p>
                    <p className="text-xs text-[#8a97a8]">{channelTotalRev > 0 ? Math.round((c.revenue / channelTotalRev) * 100) : 0}% of revenue</p>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between px-3 pt-2 text-sm">
                <span className="text-[#8a97a8]">Total</span>
                <span className="font-bold text-[#16243b]">£{channelTotalRev.toFixed(2)} · {channelTotalCount} tickets</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Channel trend (month-by-month) */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] mb-8" data-testid="channel-trend-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-[#16243b]">Channel momentum</h3>
          <span className="text-xs text-[#8a97a8]">{channelEvent === "all" ? "All events" : (eventOptions.find((o) => o.id === channelEvent)?.title || "Event")} · last 6 months</span>
        </div>
        {!trendHasData ? (
          <p className="py-8 text-center text-[#8a97a8]">No sales in the last 6 months yet.</p>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={channelTrend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="ch-online" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2ec9c9" stopOpacity={0.5} /><stop offset="100%" stopColor="#2ec9c9" stopOpacity={0.05} /></linearGradient>
                  <linearGradient id="ch-box" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f4b740" stopOpacity={0.5} /><stop offset="100%" stopColor="#f4b740" stopOpacity={0.05} /></linearGradient>
                  <linearGradient id="ch-comp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#9aa7b8" stopOpacity={0.5} /><stop offset="100%" stopColor="#9aa7b8" stopOpacity={0.05} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#8a97a8", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#8a97a8", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(val, name) => [`£${Number(val).toFixed(2)}`, CHANNEL_META[name]?.label || name]} contentStyle={{ borderRadius: 12, border: "1px solid #eef1f5" }} />
                <Legend formatter={(val) => CHANNEL_META[val]?.label || val} />
                <Area type="monotone" dataKey="online" stackId="1" stroke="#2ec9c9" strokeWidth={2} fill="url(#ch-online)" />
                <Area type="monotone" dataKey="box_office" stackId="1" stroke="#f4b740" strokeWidth={2} fill="url(#ch-box)" />
                <Area type="monotone" dataKey="comp" stackId="1" stroke="#9aa7b8" strokeWidth={2} fill="url(#ch-comp)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Refund analytics (per event) */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] mb-8" data-testid="refund-analytics-card">
        {refundAlerts.length > 0 && (
          <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4" data-testid="refund-alert-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-red-500 shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-semibold text-red-700">{refundAlerts.length} event{refundAlerts.length === 1 ? "" : "s"} above your {alertThreshold}% refund alert</p>
                <p className="text-sm text-red-600/90 mt-0.5">
                  {refundAlerts.map((a) => `${a.title} (${a.refund_rate.toFixed(1)}%)`).join(", ")}. Adjust the threshold in Settings.
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-[#f47c7c]" size={18} />
            <h3 className="font-semibold text-[#16243b]">Refund rate by event</h3>
          </div>
          {refunds && (
            <span data-testid="refund-overall-rate" className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${(refundTotals.refund_rate || 0) >= 10 ? "bg-red-100 text-red-600" : "bg-green-100 text-green-700"}`}>
              {(refundTotals.refund_rate || 0).toFixed(1)}% overall · {refundTotals.refunded || 0} of {refundTotals.sold || 0} tickets
            </span>
          )}
        </div>
        {refunds === null ? (
          <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={24} /></div>
        ) : refundEvents.length === 0 ? (
          <p className="py-8 text-center text-[#8a97a8]" data-testid="refund-empty">No refunds yet — all your sales are sticking. 🎉</p>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6 items-start">
            <div style={{ height: Math.max(224, refundEvents.length * 34) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={refundEvents} layout="vertical" margin={{ top: 0, right: 24, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" horizontal={false} />
                  <XAxis type="number" unit="%" tick={{ fill: "#8a97a8", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="title" width={110} tick={{ fill: "#5a6b80", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(val, n, p) => [`${Number(val).toFixed(1)}% (${p.payload.refunded}/${p.payload.sold})`, "Refund rate"]} contentStyle={{ borderRadius: 12, border: "1px solid #eef1f5" }} />
                  <Bar dataKey="refund_rate" radius={[0, 8, 8, 0]}>
                    {refundEvents.map((e, i) => <Cell key={i} fill={refundBarColor(e.refund_rate)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
              {refundEvents.map((e) => (
                <div key={e.product_id} className="flex items-center justify-between p-3 rounded-xl bg-[#f6f8fb]" data-testid={`refund-row-${e.product_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: refundBarColor(e.refund_rate) }} />
                    <div className="min-w-0">
                      <p className="font-medium text-[#16243b] truncate">{e.title}</p>
                      <p className="text-xs text-[#8a97a8]">{e.refunded} refunded of {e.sold} sold</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className="font-bold text-[#16243b] text-sm">{e.refund_rate.toFixed(1)}%</p>
                    <p className="text-xs text-[#f47c7c]">−£{e.refunded_amount.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {refundReasons.length > 0 && (
          <div className="mt-6 pt-5 border-t border-gray-100" data-testid="refund-reasons">
            <h4 className="text-sm font-semibold text-[#16243b] mb-3">Top refund reasons</h4>
            <div className="grid sm:grid-cols-2 gap-2">
              {refundReasons.map((r) => (
                <div key={r.reason} className="flex items-center justify-between p-3 rounded-xl bg-[#f6f8fb]" data-testid={`refund-reason-row`}>
                  <span className="text-sm text-[#16243b] truncate mr-3">{r.reason}</span>
                  <div className="text-right shrink-0">
                    <span className="text-sm font-semibold text-[#16243b]">{r.count}</span>
                    <span className="text-xs text-[#8a97a8] ml-1">· £{r.amount.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Top-selling vouchers */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] mb-8" data-testid="top-sellers-card">
        <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Trophy className="text-[#f4b740]" size={18} />
            <h3 className="font-semibold text-[#16243b]">Top events</h3>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#8a97a8]">
            Show top
            <select value={topN} onChange={(e) => setTopN(parseInt(e.target.value, 10))} data-testid="top-events-count"
              className="rounded-lg border border-gray-200 px-2 py-1 text-sm text-[#16243b] outline-none focus:border-[#2ec9c9] bg-white">
              {[5, 10, 15, 20, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            events
          </label>
        </div>
        {topSellers.length === 0 ? (
          <p className="py-8 text-center text-[#8a97a8]">No sales yet.</p>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6 items-start">
            <div style={{ height: Math.max(224, topSellers.length * 30) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topSellers} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "#8a97a8", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="title" width={110} tick={{ fill: "#5a6b80", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(val) => [`£${Number(val).toFixed(2)}`, "Revenue"]} contentStyle={{ borderRadius: 12, border: "1px solid #eef1f5" }} />
                  <Bar dataKey="revenue" radius={[0, 8, 8, 0]}>
                    {topSellers.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
              {topSellers.map((t, i) => (
                <div key={t.title} className="flex items-center justify-between p-3 rounded-xl bg-[#f6f8fb]" data-testid="top-seller-row">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0" style={{ background: barColor(i) }}>{i + 1}</span>
                    <span className="font-medium text-[#16243b] truncate">{t.title}</span>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className="font-bold text-[#16243b] text-sm">£{t.revenue.toFixed(2)}</p>
                    <p className="text-xs text-[#8a97a8]" data-testid={`top-seller-stats-${i}`}>{t.count} sold{t.refunded > 0 && <span className="text-[#ef4444] font-medium"> · {t.refunded} refunded</span>}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
